// the stylesheets first: the order of the imports is the order in the bundle, so the components' own stylesheets
// (imported by the components below) come after the base, and win from it where they overlap
import 'flag-icons/css/flag-icons.min.css';
import '@fontsource/ibm-plex-sans/400.css';
import '@fontsource/ibm-plex-sans/500.css';
import '@fontsource/ibm-plex-sans/600.css';
import '../scss/base.scss';

import {StrictMode} from 'react';
import {createRoot} from 'react-dom/client';

import {App} from './components/base/app';
import {resolveRoute} from './components/base/routing/router';
import axios from 'axios';

import {reportError, startErrorReporting} from './helpers/error-reporter';
import {reportApiFailure} from './helpers/axios-helper';
import {startPerformanceMonitoring} from './helpers/performance-monitor';
import {reloadForChunkError, startUpdateCheck} from './helpers/app-update';
import {loadLocale} from './helpers/auth-helper';
import {getEnvironment, isLoggedIn} from './helpers/session';
// sets the theme on <html> before the first render, and keeps it in sync
import './helpers/theme';

startErrorReporting();
startPerformanceMonitoring(resolveRoute);
startUpdateCheck();

// the user may have changed their language since logging in: checked once per page load, not on every request (a
// login fetches it anyway)
if (isLoggedIn()) {
    void loadLocale(getEnvironment());
}

// Bugsnag sees uncaught errors on the window, but not render errors that an error boundary caught: report those here
// (not a chunk that failed to load after a deploy: the page reloads for that)
const onRenderError = (error: unknown, info: {componentStack?: string}): void => {
    if (reloadForChunkError(error)) {
        return;
    }

    console.error(error);

    // the request of a loader (e.g. the printers), thrown by `use()`: a failed request, not a render error
    if (axios.isAxiosError(error)) {
        reportApiFailure('Load', error);
        return;
    }

    reportError(error, {section: 'react', values: {componentStack: info.componentStack}});
};

// index.html always has the #app element
createRoot(document.getElementById('app')!, {
    onCaughtError: onRenderError,
    onUncaughtError: onRenderError,
}).render(
    // in development every component is mounted twice and every effect runs twice: they must be able to start over
    <StrictMode>
        <App />
    </StrictMode>,
);
